import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Briefcase, Plus, Search, ArrowRight } from "lucide-react";

const CATEGORIES = [
  { id: "vendor", label: "Vendor" },
  { id: "employment", label: "Employment" },
  { id: "customer", label: "Customer" },
  { id: "intellectual-property", label: "IP" },
  { id: "corporate", label: "Corporate" },
  { id: "property", label: "Property" },
  { id: "consumer", label: "Consumer" },
  { id: "compliance", label: "Compliance" },
  { id: "dispute", label: "Dispute" },
  { id: "regulatory", label: "Regulatory" },
  { id: "personal", label: "Personal" },
  { id: "other", label: "Other" },
];

const STATUSES = [
  { id: "open", label: "Open" },
  { id: "on-hold", label: "On hold" },
  { id: "resolved", label: "Resolved" },
  { id: "archived", label: "Archived" },
];

function statusTone(s) {
  if (s === "resolved") return "success";
  if (s === "on-hold") return "warn";
  if (s === "archived") return "neutral";
  return "info";
}

export default function MattersListPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", category: "other", jurisdiction: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/matters", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await api.post("/matters", form);
      toast.success("Matter opened.");
      setForm({ title: "", category: "other", jurisdiction: "" });
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create matter");
    }
  };

  return (
    <div data-testid="matters-list-page">
      <PageHeader
        eyebrow="Le Registre"
        title="Matters"
        description="Every legal issue you handle — contracts, disputes, filings, obligations — organised in one place."
        action={
          <Button onClick={() => setCreating(true)} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="new-matter-btn">
            <Plus size={14} className="mr-1.5" /> New matter
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
            <Input
              placeholder="Search matters"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 border-[#E8E3F0]"
              data-testid="matters-search-input"
            />
          </div>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-[#E8E3F0] bg-white px-3 py-2 text-sm rounded-none"
          data-testid="matters-status-filter"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {/* Create dialog (inline card) */}
      {creating && (
        <form onSubmit={submit} className="border border-[#7B56C9] bg-white p-6 mb-6 space-y-4" data-testid="new-matter-form">
          <div className="label-eyebrow">Open a new matter</div>
          <div>
            <label className="text-xs text-[#4A5568] mb-1 block">Title</label>
            <Input
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. AWS renewal · Q3 2026"
              className="rounded-none border-[#E8E3F0]"
              data-testid="new-matter-title"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#4A5568] mb-1 block">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-[#E8E3F0] bg-white px-3 py-2 text-sm rounded-none"
                data-testid="new-matter-category"
              >
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#4A5568] mb-1 block">Jurisdiction (optional)</label>
              <Input
                value={form.jurisdiction}
                onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                placeholder="e.g. India, EU, US-CA"
                className="rounded-none border-[#E8E3F0]"
                data-testid="new-matter-jurisdiction"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="submit-matter-btn">Open matter</Button>
            <Button type="button" variant="outline" className="rounded-none border-[#E8E3F0]" onClick={() => setCreating(false)}>Cancel</Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="label-eyebrow">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Briefcase} title="No matters yet" description="A matter is any legal issue — a contract, a dispute, a filing. Open your first one to see how Marianne organises the rest." />
      ) : (
        <div className="border border-[#E8E3F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E3F0]">
                <th className="text-left px-4 py-3 label-eyebrow">Title</th>
                <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Category</th>
                <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Jurisdiction</th>
                <th className="text-left px-4 py-3 label-eyebrow">Status</th>
                <th className="text-left px-4 py-3 label-eyebrow hidden lg:table-cell">Updated</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b border-[#E8E3F0] hover:bg-[#FAFAF8]" data-testid={`matter-row-${m.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/app/matters/${m.id}`} className="font-medium hover:text-[#7B56C9]">{m.title}</Link>
                    {m.tags?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {m.tags.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 border border-[#E8E3F0] text-[#4A5568]">{t}</span>)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell capitalize text-xs">{m.category.replace("-", " ")}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs">{m.jurisdiction || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge tone={statusTone(m.status)}>{m.status.replace("-", " ")}</StatusBadge></td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-[#4A5568]">{new Date(m.updated_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/app/matters/${m.id}`} className="text-xs underline text-[#2A0F5A] hover:text-[#7B56C9] inline-flex items-center gap-1">
                      Open <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
